import { Box } from "@radix-ui/themes";
import capitalize from "lodash/capitalize";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";

import { SelectLeverage } from "@components/SelectLeverage";

import { useStores } from "@stores";
import { DIALOG_TYPE } from "@stores/DialogStore";

import { BN } from "@utils/BN";

import { Separator, Slider, ToggleGroup } from "@src/ui-kit";

import { AdvancedAccordion } from "../AdvancedAccordion";
import { Icon } from "../Icon";
import { OrderInput } from "../OrderInput";
import { TakeProfitStopLossAccordion } from "../TakeProfitStopLossAccordion";
import {
  MIN_GTD_LEAD_MS,
  type TradeboxTimeInForce,
  getDefaultGtdExpiryMs,
  useTradeboxInputs,
} from "../hooks/useTradeboxInputs";

import { TradeboxAccountInfo } from "./TradeboxAccountInfo";
import { TradeboxOrderButton } from "./TradeboxOrderButton";
import { TradeboxOrderDetails } from "./TradeboxOrderDetails";
import { SLIDER_RIGHT_LABEL } from "./TradeboxSliderLabel";
import {
  CloseModalButton,
  DirectionToggleWrapper,
  HeaderSection,
  InputsSection,
  LeverageWrapper,
  MarginButton,
  OrderTypeSection,
  SliderSection,
  ToggleGroupItemColored,
  ToggleGroupOrderTypeItem,
  TradeboxContainer,
} from "./styled";

interface TradeboxProps {
  inset?: boolean;
  insetClose?: () => void;
}

// Static formatter extracted outside component — never recreated
const SLIDER_VALUE_FORMATTER = (v: number) => `${v}%`;

// ---------------------------------------------------------------------------
// Memoized outer shell — only re-renders when inset/insetClose props change.
// The styled TradeboxContainer (border, background, scroll) stays stable
// while the inner observer handles all MobX reactivity.
// ---------------------------------------------------------------------------
export const Tradebox: React.FC<TradeboxProps> = React.memo(({ inset = false, insetClose }) => (
  <TradeboxContainer
    data-testid="tradebox:container"
    className="better-scroll-invisible"
    data-hide-container={inset}
    direction="column"
    gap="8px"
    width="100%"
  >
    <TradeboxInner inset={inset} insetClose={insetClose} />
  </TradeboxContainer>
));

// ---------------------------------------------------------------------------
// Inner observer — all MobX reactive logic lives here.
// Re-renders are scoped to this component only; the container shell above
// is not affected by MobX observable changes.
// ---------------------------------------------------------------------------
const TradeboxInner: React.FC<TradeboxProps> = observer(({ inset = false, insetClose }) => {
  const { t } = useTranslation();
  const { accountStore, balanceStore, dialogStore, geoFenceStore, marketStore, tradeboxStore } = useStores();

  // --- Memoized constant arrays (depend only on translation fn) ---
  const ORDER_TYPES = React.useMemo(
    () => [
      { label: t("common.limit"), value: "limit" as const, "data-testid": "tradebox:order-type-limit" },
      { label: t("common.market"), value: "market" as const, "data-testid": "tradebox:order-type-market" },
    ],
    [t],
  );

  const SLIDER_MARKS = React.useMemo(
    () => [
      { value: 0, label: t("slider.0") },
      { value: 10, label: t("slider.10") },
      { value: 20, label: t("slider.20") },
      { value: 30, label: t("slider.30") },
      { value: 40, label: t("slider.40") },
      { value: 50, label: t("slider.50") },
      { value: 60, label: t("slider.60") },
      { value: 70, label: t("slider.70") },
      { value: 80, label: t("slider.80") },
      { value: 90, label: t("slider.90") },
      { value: 100, label: t("slider.100") },
    ],
    [t],
  );

  const DIRECTION_TABS = React.useMemo(
    () => [
      { label: t("common.long"), value: "long" as const, "data-testid": "tradebox:direction-long" },
      { label: t("common.short"), value: "short" as const, "data-testid": "tradebox:direction-short" },
    ],
    [t],
  );

  const [isCreatingOrder, setIsCreatingOrder] = React.useState(false);
  const [timeInForce, setTimeInForce] = React.useState<TradeboxTimeInForce>("GTC");
  const [gtdExpireAtMs, setGtdExpireAtMs] = React.useState(getDefaultGtdExpiryMs);
  const [reduceOnly, setReduceOnly] = React.useState(false);
  const initialDirection = dialogStore.tradeboxInitialDirection;
  const {
    limitPrice,
    size,
    total,
    direction,
    slider,
    userLeveragableBalance,
    availableMargin,
    leverage,
    setLimitPrice,
    setSize,
    setTotal,
    setDirection,
    setSlider,
    setLeverage,
    orderType,
    setOrderType,
    create,
    validate,
    isLeverageDialogOpen,
    setIsLeverageDialogOpen,
    tpSlData,
    setTpSlData,
  } = useTradeboxInputs(initialDirection);

  const isConnected = accountStore.isAuthenticated;
  const isAvailable = availableMargin.amount.gt(BN.ZERO);
  const gtdExpireInvalid =
    timeInForce === "GTD" &&
    (gtdExpireAtMs == null || !Number.isFinite(gtdExpireAtMs) || gtdExpireAtMs <= Date.now() + MIN_GTD_LEAD_MS);

  const isButtonDisabled = validate.limitPrice || validate.size || validate.total || validate.empty || gtdExpireInvalid;
  const directionLabel = capitalize(direction);

  const [token, setToken] = React.useState<"base" | "quote">("quote");

  const marketParams = marketStore.getMarketConfig(marketStore.activeMarket?.addr ?? "");

  // --- Memoized handlers (stable refs for child components) ---
  const handleDirectionChange = React.useCallback(
    (v: "long" | "short") => {
      setDirection(v);
      setTpSlData({
        takeProfitPrice: BN.ZERO,
        gainPercent: BN.ZERO,
        stopLossPrice: BN.ZERO,
        lossPercent: BN.ZERO,
      });
    },
    [setDirection, setTpSlData],
  );

  const handleOrderTypeChange = React.useCallback(
    (v: "market" | "limit") => {
      setOrderType(v);
      if (v === "market") {
        setTimeInForce("GTC");
      }
    },
    [setOrderType],
  );

  const handleTimeInForceChange = React.useCallback((v: TradeboxTimeInForce) => {
    setTimeInForce(v);
    if (v === "GTD") {
      setGtdExpireAtMs((prev) => (prev > Date.now() + MIN_GTD_LEAD_MS ? prev : getDefaultGtdExpiryMs()));
    }
  }, []);

  const handleSliderChange = React.useCallback(
    (value: number | number[]) => {
      setSlider(Array.isArray(value) ? value[0] : value);
    },
    [setSlider],
  );

  const handleCreateOrder = React.useCallback(async () => {
    try {
      setIsCreatingOrder(true);
      await create(timeInForce, reduceOnly, timeInForce === "GTD" ? gtdExpireAtMs : undefined);
    } finally {
      setIsCreatingOrder(false);
    }

    if (inset) {
      insetClose?.();
    }
  }, [create, timeInForce, reduceOnly, gtdExpireAtMs, inset, insetClose]);

  const handleDepositClick = React.useCallback(() => {
    dialogStore.open(DIALOG_TYPE.DEPOSIT);
  }, [dialogStore]);

  const handleTransferClick = React.useCallback(() => {
    dialogStore.open(DIALOG_TYPE.TRANSFER);
  }, [dialogStore]);

  const handleMaxClick = React.useCallback(() => {
    setSlider(100);
  }, [setSlider]);

  const handleTokenToggle = React.useCallback(() => {
    setToken((prev) => (prev === "base" ? "quote" : "base"));
  }, []);

  const handleLastPriceClick = React.useCallback(() => {
    if (!marketStore.marketPrice) return;
    tradeboxStore.setRequiredPrice(marketStore.marketPrice.toNumber());
  }, [marketStore, tradeboxStore]);

  const handleClearLimitPrice = React.useCallback(() => {
    tradeboxStore.setRequiredPrice(0);
    setLimitPrice(BN.ZERO);
  }, [tradeboxStore]);

  const handleClearSizeOrTotal = React.useCallback(() => {
    if (token === "base") {
      setSize(BN.ZERO);
    } else {
      setTotal(BN.ZERO);
    }
  }, [token, setSize, setTotal]);

  const handleConnectWallet = React.useCallback(() => {
    accountStore.initConnectionFlow();
  }, [accountStore]);

  const handleOpenLeverageDialog = React.useCallback(() => {
    setIsLeverageDialogOpen(true);
  }, [setIsLeverageDialogOpen]);

  // --- Memoized TP/SL handlers ---
  const handleTakeProfitPriceChange = React.useCallback(
    (v: BN) => setTpSlData({ ...tpSlData, takeProfitPrice: v }),
    [tpSlData, setTpSlData],
  );

  const handleGainPercentChange = React.useCallback(
    (v: BN) => setTpSlData({ ...tpSlData, gainPercent: v }),
    [tpSlData, setTpSlData],
  );

  const handleStopLossPriceChange = React.useCallback(
    (v: BN) => setTpSlData({ ...tpSlData, stopLossPrice: v }),
    [tpSlData, setTpSlData],
  );

  const handleLossPercentChange = React.useCallback(
    (v: BN) => setTpSlData({ ...tpSlData, lossPercent: v }),
    [tpSlData, setTpSlData],
  );

  // --- Memoized input data ---
  const activeInputData = React.useMemo(
    () =>
      token === "base"
        ? {
            decimalScale: 0,
            displayDecimals: marketParams.pricePrecision,
            error: validate.size,
            rightLabel: marketStore.baseToken?.symbol,
            value: size,
            onChange: setSize,
          }
        : {
            decimalScale: 0,
            displayDecimals: 2,
            error: validate.total,
            rightLabel: marketStore.quoteToken?.symbol,
            value: total,
            onChange: setTotal,
          },
    [
      token,
      marketParams.pricePrecision,
      validate.size,
      validate.total,
      marketStore.baseToken?.symbol,
      marketStore.quoteToken?.symbol,
      size,
      total,
      setSize,
      setTotal,
    ],
  );

  const tpSlSide = direction === "long" ? "buy" : "sell";
  const tpSlEntryPrice = React.useMemo(
    () => (orderType === "market" ? new BN(marketStore.marketPrice) : limitPrice),
    [orderType, marketStore.marketPrice, limitPrice],
  );
  const isMaxDisabled = userLeveragableBalance.amount.eq(total) && userLeveragableBalance.amount.gt(BN.ZERO);

  // --- Memoized JSX sections ---
  const headerSection = React.useMemo(
    () => (
      <>
        <HeaderSection align="center" justify="between">
          <DirectionToggleWrapper>
            <ToggleGroup
              data-testid="tradebox:direction"
              ItemComponent={ToggleGroupItemColored}
              data-direction={direction}
              options={DIRECTION_TABS}
              value={direction}
              fullWidth
              onChange={handleDirectionChange}
            />
          </DirectionToggleWrapper>
          <LeverageWrapper>
            <SelectLeverage
              data-testid="tradebox:leverage-dialog"
              isOpen={isLeverageDialogOpen}
              leverage={leverage}
              onChange={setLeverage}
              onOpenChange={setIsLeverageDialogOpen}
            />
            <MarginButton
              data-testid="tradebox:leverage-button"
              disabled={!isConnected || geoFenceStore.isRestricted}
              variant="solid"
              onClick={handleOpenLeverageDialog}
            >
              {leverage}x
            </MarginButton>
          </LeverageWrapper>
          {inset && (
            <CloseModalButton aria-label="Close tradebox" variant="outline" onClick={insetClose}>
              <Icon color="gray-11" icon="ri-close-large-line" />
            </CloseModalButton>
          )}
        </HeaderSection>
        <Separator color="gray-5" size="4" />
      </>
    ),
    [
      direction,
      DIRECTION_TABS,
      handleDirectionChange,
      isLeverageDialogOpen,
      leverage,
      setLeverage,
      setIsLeverageDialogOpen,
      handleOpenLeverageDialog,
      isConnected,
      geoFenceStore.isRestricted,
      inset,
      insetClose,
    ],
  );

  const orderTypeSection = React.useMemo(
    () => (
      <OrderTypeSection>
        <ToggleGroup
          data-testid="tradebox:order-type"
          ItemComponent={ToggleGroupOrderTypeItem}
          data-direction={orderType}
          options={ORDER_TYPES}
          value={orderType}
          variant="secondary"
          fullWidth
          onChange={handleOrderTypeChange}
        />
      </OrderTypeSection>
    ),
    [orderType, ORDER_TYPES, handleOrderTypeChange],
  );

  const inputsSection = React.useMemo(
    () => (
      <InputsSection direction="column" gap="8px" width="100%">
        {orderType !== "market" && (
          <OrderInput
            data-testid="tradebox:input-limit-price"
            inputTestId="tradebox:limit-price:input"
            errorTestId="tradebox:limit-price:error"
            rightActionTestId="tradebox:input-limit-price:last"
            clearActionTestId="tradebox:input-limit-price:clear"
            decimalScale={0}
            displayDecimals={marketParams.pricePrecision}
            error={validate.limitPrice}
            label={t("tradebox.limitPrice")}
            rightAction={t("common.last")}
            rightActionDisabled={false}
            clearAction="Clear"
            clearActionDisabled={limitPrice.isZero()}
            value={limitPrice}
            onChange={setLimitPrice}
            onRightActionClick={handleLastPriceClick}
            onClearActionClick={handleClearLimitPrice}
          />
        )}
        <OrderInput
          {...activeInputData}
          data-testid="tradebox:input-size"
          inputTestId="tradebox:size:input"
          errorTestId="tradebox:size:error"
          rightActionTestId="tradebox:input-size:max"
          clearActionTestId="tradebox:input-size:clear"
          rightLabelTestId="tradebox:input-size:switch"
          label={t("common.size")}
          rightAction={t("common.max")}
          rightActionDisabled={isMaxDisabled}
          clearAction="Clear"
          clearActionDisabled={activeInputData.value.isZero()}
          onRightActionClick={handleMaxClick}
          onRightLabelClick={handleTokenToggle}
          onClearActionClick={handleClearSizeOrTotal}
        />
      </InputsSection>
    ),
    [
      orderType,
      marketParams.pricePrecision,
      validate.limitPrice,
      t,
      limitPrice,
      setLimitPrice,
      handleLastPriceClick,
      handleClearLimitPrice,
      activeInputData,
      isMaxDisabled,
      handleMaxClick,
      handleTokenToggle,
      handleClearSizeOrTotal,
    ],
  );

  const sliderSection = React.useMemo(
    () =>
      isConnected ? (
        <SliderSection>
          <Slider
            data-testid="tradebox:slider"
            disabled={!isAvailable}
            leftLabel={t("tradebox.positionSize")}
            marks={SLIDER_MARKS}
            rightLabel={SLIDER_RIGHT_LABEL}
            value={slider}
            valueFormatter={SLIDER_VALUE_FORMATTER}
            hideLabels
            onChange={handleSliderChange}
          />
        </SliderSection>
      ) : null,
    [isConnected, isAvailable, t, SLIDER_MARKS, slider, handleSliderChange],
  );

  const advancedSection = React.useMemo(
    () => (
      <AdvancedAccordion
        data-testid="tradebox:advanced"
        orderType={orderType}
        reduceOnly={reduceOnly}
        timeInForce={timeInForce}
        gtdExpireAtMs={gtdExpireAtMs}
        gtdExpireInvalid={gtdExpireInvalid}
        onReduceOnlyChange={setReduceOnly}
        onTimeInForceChange={handleTimeInForceChange}
        onGtdExpireAtMsChange={setGtdExpireAtMs}
      />
    ),
    [orderType, reduceOnly, timeInForce, gtdExpireAtMs, gtdExpireInvalid, handleTimeInForceChange, setReduceOnly],
  );

  const tpSlSection = React.useMemo(
    () => (
      <TakeProfitStopLossAccordion
        data-testid="tradebox:tp-sl"
        amount={size}
        decimalScale={marketStore.quoteToken?.decimals ?? 8}
        displayDecimals={marketParams.pricePrecision}
        entryPrice={tpSlEntryPrice}
        gainPercent={tpSlData.gainPercent}
        lossPercent={tpSlData.lossPercent}
        side={tpSlSide}
        stopLossPrice={tpSlData.stopLossPrice}
        takeProfitPrice={tpSlData.takeProfitPrice}
        onGainPercentChange={handleGainPercentChange}
        onLossPercentChange={handleLossPercentChange}
        onStopLossPriceChange={handleStopLossPriceChange}
        onTakeProfitPriceChange={handleTakeProfitPriceChange}
      />
    ),
    [
      size,
      marketStore.quoteToken?.decimals,
      marketParams.pricePrecision,
      tpSlEntryPrice,
      tpSlData,
      tpSlSide,
      handleGainPercentChange,
      handleLossPercentChange,
      handleStopLossPriceChange,
      handleTakeProfitPriceChange,
    ],
  );

  // Compute button state as primitives — React.memo compares by value
  const buttonExceedsMax = React.useMemo(() => {
    if (total.eq(BN.ZERO)) return false;
    const takerFeeRateRaw = marketStore.activeMarket?.taker_fee_rate ?? 0;
    const takerFeeRate = new BN(takerFeeRateRaw).div(1_000_000);
    const denom = takerFeeRate.plus(new BN(1).div(leverage));
    const maxTotal = denom.isZero() ? BN.ZERO : availableMargin.amount.div(denom);
    return total.gt(maxTotal);
  }, [total, leverage, availableMargin.amount, marketStore.activeMarket?.taker_fee_rate]);

  const buttonHasFundingBalance = marketStore.quoteToken
    ? balanceStore.getFundingVaultBalanceByAddress(marketStore.quoteToken.address).amount.gt(BN.ZERO)
    : false;
  const buttonTradingIsEmpty = availableMargin.amount.eq(BN.ZERO);

  const buttonIsEmpty = total.eq(BN.ZERO) || size.eq(BN.ZERO);
  const buttonSizeFormatted = size.toFixed(marketParams.pricePrecision);
  const buttonBaseSymbol = marketStore.baseToken?.symbol;

  return (
    <>
      {headerSection}
      {orderTypeSection}
      {inputsSection}
      {sliderSection}
      <Box height="0px" />
      {advancedSection}
      {tpSlSection}
      <TradeboxOrderDetails
        size={size}
        direction={direction}
        leverage={leverage}
        limitPrice={limitPrice}
        orderType={orderType}
        total={total}
      />
      <TradeboxOrderButton
        isConnected={isConnected}
        isRestricted={geoFenceStore.isRestricted}
        exceedsMax={buttonExceedsMax}
        hasFundingBalance={buttonHasFundingBalance}
        tradingIsEmpty={buttonTradingIsEmpty}
        isEmpty={buttonIsEmpty}
        direction={direction}
        directionLabel={directionLabel}
        isButtonDisabled={isButtonDisabled}
        isCreatingOrder={isCreatingOrder}
        sizeFormatted={buttonSizeFormatted}
        baseSymbol={buttonBaseSymbol}
        handleCreateOrder={handleCreateOrder}
        handleDepositClick={handleDepositClick}
        handleTransferClick={handleTransferClick}
        handleConnectWallet={handleConnectWallet}
      />
      {isConnected && <TradeboxAccountInfo />}
    </>
  );
});
